import clsx from 'clsx'
import type { InterestTopic } from '@app/shared'

import { SparkleSmall } from './icons'
import { Unavailable } from './parts'

// ============================================================================
// THE CLUSTER ROW — second on the page, and second in the argument.
//
// README §6: "Clusters are auto-grouped, and the footer states `clusters
// regrouped nightly` so the grouping never looks hand-made." That footer is the
// honest bit and it is not decoration: these groups are a machine's current
// opinion, redone every night, and a row of chips that looked like folders would
// be claiming the owner filed them.
//
// `Summarize these 14` is the cluster-scale version of the ask bar, and it waits
// on the same thing — so it renders as designed, keeps its focus ring, and says
// V4 on hover rather than doing nothing when pressed.
// ============================================================================

export function ClusterRow({
  topics,
  activeId,
  onSelect,
  isPending,
  error,
}: {
  topics?: InterestTopic[]
  activeId?: string
  onSelect: (id: string) => void
  isPending: boolean
  error: unknown
}) {
  if (error) return <Unavailable error={error} className="flex-none" />

  if (isPending || !topics) {
    return (
      <div className="flex flex-none items-center gap-[9px]" aria-hidden>
        {[112, 128, 118, 84, 92].map((width, i) => (
          <div
            key={i}
            className="h-[29px] animate-pulse rounded-pill border border-line bg-surface"
            style={{ width }}
          />
        ))}
      </div>
    )
  }

  const active = topics.find((t) => t.id === activeId)

  return (
    <div className="flex flex-none items-center gap-[9px]">
      {topics.map((topic) => {
        const isActive = topic.id === activeId
        return (
          <button
            key={topic.id}
            type="button"
            aria-pressed={isActive}
            onClick={() => onSelect(topic.id)}
            className={clsx(
              'flex flex-none items-center gap-2 whitespace-nowrap rounded-pill px-[13px] py-[7px] leading-none',
              'transition-colors duration-[var(--dur-fast)] ease-[var(--ease-out)]',
              isActive
                ? 'bg-ink text-[12px] font-semibold text-surface'
                : 'border border-line bg-surface text-[12px] font-medium text-ink-2 hover:bg-surface-2',
            )}
          >
            {topic.label}
            <span className={clsx('font-medium', isActive ? 'text-muted' : 'text-microcopy')}>
              {topic.count}
            </span>
          </button>
        )
      })}

      {topics.length > 0 && <span className="mx-[3px] h-4 w-px flex-none bg-dash" aria-hidden />}

      {active && (
        <button
          type="button"
          aria-disabled="true"
          title="Chat is live, but it cannot read your saves: they are not in the index it retrieves from, so a summary of this cluster would have nothing to cite. Indexing saves is what unblocks this — the same thing the ask bar is waiting for."
          onClick={(e) => e.preventDefault()}
          className="flex flex-none cursor-not-allowed items-center gap-[7px] whitespace-nowrap rounded-pill border border-accent-line bg-accent-fill px-[13px] py-[7px] text-[11.5px] font-semibold leading-none text-accent"
        >
          <SparkleSmall size={12} className="text-chat" />
          Summarize these {active.count} →
        </button>
      )}

      <span className="ml-auto flex-none text-[11px] leading-none text-microcopy">
        clusters regrouped nightly
      </span>
    </div>
  )
}
